import { Ics2ReferralSubmission, Ics2SubmissionStatus } from 'store/declarations/eu/ics2/customs-requests/types';
import { getSubmissionReferralIds } from 'store/declarations/eu/ics2/customs-requests/utils';
import { validateReferralDraft } from './draftValidation';
import {
    Ics2DocumentsByReferral,
    Ics2ReferralRow,
    getReferralAttachments,
    toAttachmentContext,
} from './referralDocuments';

export type SendContext = {
    postcodeOptionalCountries: string[];
    documents: Ics2DocumentsByReferral;
};

export type SendBlocker = { title: string; description: string };

export const NOT_SAVED_MESSAGE = 'No answer has been saved yet.';

export const draftVersionsOf = (rows: Ics2ReferralRow[]) =>
    rows.reduce<Record<string, number>>((versions, { referral }) => {
        versions[referral.id] = referral.draftVersion;
        return versions;
    }, {});

export const getDraftErrors = (row: Ics2ReferralRow, { postcodeOptionalCountries, documents }: SendContext) =>
    row.referral.draftVersion
        ? validateReferralDraft(row.request, row.referral, {
              postcodeOptionalCountries,
              ...toAttachmentContext(getReferralAttachments(documents, row)),
          })
        : [NOT_SAVED_MESSAGE];

export const findSendBlocker = (rows: Ics2ReferralRow[], context: SendContext): SendBlocker | undefined => {
    const unsaved = rows.filter(({ referral }) => !referral.draftVersion);
    if (unsaved.length) {
        return {
            title: 'Save every answer first',
            description: `No answer has been saved for ${unsaved
                .map(({ referral }) => referral.referralRequestReference)
                .join(', ')}.`,
        };
    }

    const invalid = rows
        .map((row) => ({ row, errors: getDraftErrors(row, context) }))
        .filter(({ errors }) => errors.length);
    if (!invalid.length) return undefined;

    return {
        title: 'Some answers are incomplete',
        description: invalid
            .map(({ row, errors }) => `${row.referral.referralRequestReference}: ${errors.join('; ')}`)
            .join(' — '),
    };
};

export const getDispatchErrors = (submissions: Ics2ReferralSubmission[]) =>
    submissions
        .filter(
            ({ status, lastError }) =>
                lastError && (status === Ics2SubmissionStatus.BLOCKED || status === Ics2SubmissionStatus.REJECTED)
        )
        .reduce<Record<string, string>>((all, submission) => {
            getSubmissionReferralIds(submission).forEach((id) => {
                if (!all[id]) all[id] = submission.lastError as string;
            });
            return all;
        }, {});
